import { useState, useEffect } from 'react';
import { ChevronLeft, Mic, Volume2, PlayCircle, StopCircle, ToggleLeft, ToggleRight, Sliders } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface VoiceSettings {
    inputDevice: string;
    outputDevice: string;
    voiceActivation: boolean;
    voiceActivationThreshold: number;
    speechRate: number;
    speakResponses: boolean;
    pushToTalkMode: boolean;
    noiseReduction: boolean;
    autoStopListening: number; // seconds
}

const STORAGE_KEY = 'way2will_voice_settings';

const defaultSettings: VoiceSettings = {
    inputDevice: 'default',
    outputDevice: 'default',
    voiceActivation: true,
    voiceActivationThreshold: 50,
    speechRate: 1.0,
    speakResponses: true,
    pushToTalkMode: false,
    noiseReduction: true,
    autoStopListening: 5,
};

export default function AppVoiceSettings() {
    const navigate = useNavigate();
    const [settings, setSettings] = useState<VoiceSettings>(defaultSettings);
    const [isTesting, setIsTesting] = useState(false);
    const [micLevel, setMicLevel] = useState(0);
    const [audioDevices, setAudioDevices] = useState<{ inputs: MediaDeviceInfo[]; outputs: MediaDeviceInfo[] }>({
        inputs: [],
        outputs: [],
    });

    // Load settings
    useEffect(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            try {
                setSettings({ ...defaultSettings, ...JSON.parse(stored) });
            } catch (e) {
                console.error('Error loading voice settings:', e);
            }
        }
    }, []);

    // Get available audio devices - with safety check for mobile
    useEffect(() => {
        const getDevices = async () => {
            // Safety check for browsers that don't support mediaDevices
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                console.warn('mediaDevices API not available');
                return;
            }

            try {
                // Request microphone permission to get device labels
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                // Stop the stream immediately - we just needed permission
                stream.getTracks().forEach(track => track.stop());

                const devices = await navigator.mediaDevices.enumerateDevices();
                setAudioDevices({
                    inputs: devices.filter(d => d.kind === 'audioinput'),
                    outputs: devices.filter(d => d.kind === 'audiooutput'),
                });
            } catch (e) {
                console.error('Error getting audio devices:', e);
                // Don't crash - just leave devices empty
            }
        };
        getDevices();
    }, []);

    const updateSettings = (updates: Partial<VoiceSettings>) => {
        const updated = { ...settings, ...updates };
        setSettings(updated);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    };

    const testMicrophone = async () => {
        if (isTesting) {
            setIsTesting(false);
            setMicLevel(0);
            return;
        }

        // Safety check
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            alert('Microphone access is not supported on this device.');
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const audioContext = new AudioContext();
            const analyser = audioContext.createAnalyser();
            const microphone = audioContext.createMediaStreamSource(stream);
            microphone.connect(analyser);
            analyser.fftSize = 256;

            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            setIsTesting(true);

            const updateLevel = () => {
                if (!isTesting) {
                    stream.getTracks().forEach(track => track.stop());
                    audioContext.close();
                    return;
                }
                analyser.getByteFrequencyData(dataArray);
                const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
                setMicLevel(Math.min(100, average * 2));
                requestAnimationFrame(updateLevel);
            };
            updateLevel();

            // Auto-stop after 10 seconds
            setTimeout(() => {
                setIsTesting(false);
                setMicLevel(0);
                stream.getTracks().forEach(track => track.stop());
                audioContext.close();
            }, 10000);
        } catch (e) {
            console.error('Error testing microphone:', e);
            alert('Microphone access denied. Please enable microphone permissions.');
        }
    };

    const testSpeaker = () => {
        // Safety check
        if (!window.speechSynthesis) {
            alert('Speech synthesis is not supported on this device.');
            return;
        }
        const utterance = new SpeechSynthesisUtterance('Testing audio output. Hello from Way2Will!');
        utterance.rate = settings.speechRate;
        window.speechSynthesis.speak(utterance);
    };

    return (
        <div className="px-4 pb-8">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <button
                    onClick={() => navigate(-1)}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F8FA] dark:bg-dark-elevated"
                >
                    <ChevronLeft className="w-5 h-5 text-[#4A5C6A]" />
                </button>
                <h1 className="text-xl font-bold text-[#1A2832] dark:text-white">Voice Settings</h1>
            </div>

            {/* Microphone Settings */}
            <section className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm mb-4">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#D9EEF2] flex items-center justify-center">
                        <Mic className="w-5 h-5 text-[#0D5F6B]" />
                    </div>
                    <div>
                        <h2 className="text-base font-semibold text-[#1A2832] dark:text-white">Microphone</h2>
                        <p className="text-xs text-[#7B8C9B]">Configure voice input</p>
                    </div>
                </div>

                {/* Input Device */}
                <div className="mb-4">
                    <label className="block text-xs text-[#7B8C9B] mb-2">Input Device</label>
                    <select
                        value={settings.inputDevice}
                        onChange={(e) => updateSettings({ inputDevice: e.target.value })}
                        className="w-full px-3 py-2 rounded-xl border border-[#DCE4EA] dark:border-dark-border bg-white dark:bg-dark-elevated text-[#1A2832] dark:text-white text-sm"
                    >
                        <option value="default">System Default</option>
                        {audioDevices.inputs.map((device) => (
                            <option key={device.deviceId} value={device.deviceId}>
                                {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Test Microphone */}
                <div className="mb-4">
                    <button
                        onClick={testMicrophone}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm ${isTesting
                            ? 'bg-[#D66B5C] text-white'
                            : 'bg-[#0D5F6B] text-white'
                            }`}
                    >
                        {isTesting ? (
                            <>
                                <StopCircle className="w-4 h-4" />
                                Stop Test
                            </>
                        ) : (
                            <>
                                <PlayCircle className="w-4 h-4" />
                                Test Microphone
                            </>
                        )}
                    </button>
                    {isTesting && (
                        <div className="mt-3">
                            <div className="h-2 bg-[#F5F8FA] rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-[#0D5F6B] transition-all duration-100"
                                    style={{ width: `${micLevel}%` }}
                                />
                            </div>
                            <p className="text-xs text-[#7B8C9B] mt-1">Speak to test your microphone</p>
                        </div>
                    )}
                </div>

                {/* Voice Activation */}
                <div className="flex items-center justify-between py-3 border-t border-[#E5EBF0] dark:border-dark-border">
                    <div>
                        <span className="text-sm text-[#1A2832] dark:text-white">Voice Activation</span>
                        <p className="text-xs text-[#7B8C9B]">Start listening when you speak</p>
                    </div>
                    <button onClick={() => updateSettings({ voiceActivation: !settings.voiceActivation })}>
                        {settings.voiceActivation ? (
                            <ToggleRight className="w-10 h-6 text-[#0D5F6B]" />
                        ) : (
                            <ToggleLeft className="w-10 h-6 text-[#7B8C9B]" />
                        )}
                    </button>
                </div>

                {/* Push to Talk */}
                <div className="flex items-center justify-between py-3 border-t border-[#E5EBF0] dark:border-dark-border">
                    <div>
                        <span className="text-sm text-[#1A2832] dark:text-white">Push to Talk Mode</span>
                        <p className="text-xs text-[#7B8C9B]">Hold button to speak</p>
                    </div>
                    <button onClick={() => updateSettings({ pushToTalkMode: !settings.pushToTalkMode })}>
                        {settings.pushToTalkMode ? (
                            <ToggleRight className="w-10 h-6 text-[#0D5F6B]" />
                        ) : (
                            <ToggleLeft className="w-10 h-6 text-[#7B8C9B]" />
                        )}
                    </button>
                </div>

                {/* Noise Reduction */}
                <div className="flex items-center justify-between py-3 border-t border-[#E5EBF0] dark:border-dark-border">
                    <div>
                        <span className="text-sm text-[#1A2832] dark:text-white">Noise Reduction</span>
                        <p className="text-xs text-[#7B8C9B]">Filter background noise</p>
                    </div>
                    <button onClick={() => updateSettings({ noiseReduction: !settings.noiseReduction })}>
                        {settings.noiseReduction ? (
                            <ToggleRight className="w-10 h-6 text-[#0D5F6B]" />
                        ) : (
                            <ToggleLeft className="w-10 h-6 text-[#7B8C9B]" />
                        )}
                    </button>
                </div>
            </section>

            {/* Speaker Settings */}
            <section className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm mb-4">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#D9EEF2] flex items-center justify-center">
                        <Volume2 className="w-5 h-5 text-[#0D5F6B]" />
                    </div>
                    <div>
                        <h2 className="text-base font-semibold text-[#1A2832] dark:text-white">Audio Output</h2>
                        <p className="text-xs text-[#7B8C9B]">Configure AI voice responses</p>
                    </div>
                </div>

                {/* Output Device */}
                <div className="mb-4">
                    <label className="block text-xs text-[#7B8C9B] mb-2">Output Device</label>
                    <select
                        value={settings.outputDevice}
                        onChange={(e) => updateSettings({ outputDevice: e.target.value })}
                        className="w-full px-3 py-2 rounded-xl border border-[#DCE4EA] dark:border-dark-border bg-white dark:bg-dark-elevated text-[#1A2832] dark:text-white text-sm"
                    >
                        <option value="default">System Default</option>
                        {audioDevices.outputs.map((device) => (
                            <option key={device.deviceId} value={device.deviceId}>
                                {device.label || `Speaker ${device.deviceId.slice(0, 8)}`}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Test Speaker */}
                <button
                    onClick={testSpeaker}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm bg-[#0D5F6B] text-white mb-4"
                >
                    <PlayCircle className="w-4 h-4" />
                    Test Speaker
                </button>

                {/* Speak Responses */}
                <div className="flex items-center justify-between py-3 border-t border-[#E5EBF0] dark:border-dark-border">
                    <div>
                        <span className="text-sm text-[#1A2832] dark:text-white">Speak AI Responses</span>
                        <p className="text-xs text-[#7B8C9B]">AI reads responses aloud</p>
                    </div>
                    <button onClick={() => updateSettings({ speakResponses: !settings.speakResponses })}>
                        {settings.speakResponses ? (
                            <ToggleRight className="w-10 h-6 text-[#0D5F6B]" />
                        ) : (
                            <ToggleLeft className="w-10 h-6 text-[#7B8C9B]" />
                        )}
                    </button>
                </div>

                {/* Speech Rate */}
                <div className="py-3 border-t border-[#E5EBF0] dark:border-dark-border">
                    <div className="flex justify-between mb-2">
                        <span className="text-sm text-[#1A2832] dark:text-white">Speech Rate</span>
                        <span className="text-sm text-[#0D5F6B] font-medium">{settings.speechRate.toFixed(1)}x</span>
                    </div>
                    <input
                        type="range"
                        min="0.5"
                        max="2"
                        step="0.1"
                        value={settings.speechRate}
                        onChange={(e) => updateSettings({ speechRate: parseFloat(e.target.value) })}
                        className="w-full accent-[#0D5F6B]"
                    />
                    <div className="flex justify-between text-xs text-[#7B8C9B] mt-1">
                        <span>Slower</span>
                        <span>Faster</span>
                    </div>
                </div>
            </section>

            {/* Advanced Settings */}
            <section className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#D9EEF2] flex items-center justify-center">
                        <Sliders className="w-5 h-5 text-[#0D5F6B]" />
                    </div>
                    <div>
                        <h2 className="text-base font-semibold text-[#1A2832] dark:text-white">Advanced</h2>
                        <p className="text-xs text-[#7B8C9B]">Fine-tune voice detection</p>
                    </div>
                </div>

                {/* Voice Activation Threshold */}
                <div className="py-3">
                    <div className="flex justify-between mb-2">
                        <span className="text-sm text-[#1A2832] dark:text-white">Activation Sensitivity</span>
                        <span className="text-sm text-[#0D5F6B] font-medium">{settings.voiceActivationThreshold}%</span>
                    </div>
                    <input
                        type="range"
                        min="10"
                        max="90"
                        step="5"
                        value={settings.voiceActivationThreshold}
                        onChange={(e) => updateSettings({ voiceActivationThreshold: parseInt(e.target.value) })}
                        className="w-full accent-[#0D5F6B]"
                    />
                    <div className="flex justify-between text-xs text-[#7B8C9B] mt-1">
                        <span>More Sensitive</span>
                        <span>Less Sensitive</span>
                    </div>
                </div>

                {/* Auto Stop */}
                <div className="py-3 border-t border-[#E5EBF0] dark:border-dark-border">
                    <div className="flex justify-between mb-2">
                        <span className="text-sm text-[#1A2832] dark:text-white">Auto-stop after silence</span>
                        <span className="text-sm text-[#0D5F6B] font-medium">{settings.autoStopListening}s</span>
                    </div>
                    <input
                        type="range"
                        min="2"
                        max="10"
                        step="1"
                        value={settings.autoStopListening}
                        onChange={(e) => updateSettings({ autoStopListening: parseInt(e.target.value) })}
                        className="w-full accent-[#0D5F6B]"
                    />
                </div>
            </section>
        </div>
    );
}
